import { assertNever, getErrorKind } from "./errorKind";
import { getErrorStatusInfo, isOptimisticLockConflict, } from "./getErrorCode";
var GENERIC = "ui-czna-users.error.generic";
/** What {@link getUserErrorMessageId} answers when it has nothing more specific. */
export var GENERIC_USER_ERROR_ID = GENERIC;
/**
 * CZNA-1626 (UC ER-04) — the failures whose ADVICE differs from "try again", each with its own
 * sentence: ask for the permission, sign in again, wait for the server, check the connection.
 * The 401/403/network sentences are ui-czna-authorities' (CZNA-591) word for word, so both
 * modules say the same thing.
 *
 * `undefined` for every other kind: those carry nothing this function can say better than
 * the caller's own fallback (a specific backend message, or the generic sentence). The
 * exhaustive switch makes a new {@link ErrorKind} a compile error here until it is answered.
 *
 * Used by every formatter in this module — the user and organization ladders below only as
 * their last step, after the specific messages, and the API client screens
 * (`apiClientErrorMessage.ts`) before the backend's raw message.
 */
export var messageIdForStatusKind = function (kind) {
    switch (kind) {
        case "unauthenticated":
            return "ui-czna-users.error.unauthenticated";
        case "forbidden":
            return "ui-czna-users.error.forbidden";
        case "server":
            return "ui-czna-users.error.server";
        case "network":
            return "ui-czna-users.error.network";
        case "validation":
        case "notFound":
        case "conflict":
        case "unprocessable":
        case "unknown":
            return undefined;
        default:
            return assertNever(kind);
    }
};
/** {@link messageIdForStatusKind} of the failure `info` describes. */
export var getStatusMessageId = function (info) {
    return messageIdForStatusKind(getErrorKind(info));
};
/**
 * CZNA-1626 — what a detail pane says when loading its record failed: one of the four
 * {@link messageIdForStatusKind} sentences for a 401, 403, 5xx or a failure without a
 * response, otherwise (a 404, any other status, or no error but no data either) the pane's
 * own not-found message.
 *
 * @param error the failed query's `error` (`null`/`undefined` when the query did not fail)
 */
export var getLoadFailureMessageId = function (error, notFoundMessageId) {
    var _a;
    return (_a = (error ? getStatusMessageId(getErrorStatusInfo(error)) : undefined)) !== null && _a !== void 0 ? _a : notFoundMessageId;
};
export var extractActiveUserCount = function (message) {
    if (!message)
        return 0;
    var match = message.match(/(\d+)\s+active\s+user/i);
    return match ? parseInt(match[1], 10) : 0;
};
/**
 * CZNA-1591: the rule codes mod-czna-users sends (PasswordPolicyRule) with a 400 when
 * Keycloak's password policy refuses a password an administrator set. A code this app does
 * not know yet (a policy added to the realm later) gets the general policy message.
 */
var PASSWORD_POLICY_RULES = [
    "MIN_LENGTH",
    "UPPER_CASE",
    "DIGIT",
    "SPECIAL_CHARACTER",
    "NO_DIACRITICS",
    "OTHER",
];
var isPasswordPolicyRule = function (rule) {
    return PASSWORD_POLICY_RULES.includes(rule);
};
export var getUserErrorMessageId = function (info) {
    var _a;
    if (info.status === 400 && info.rule) {
        var rule = isPasswordPolicyRule(info.rule) ? info.rule : "OTHER";
        return "ui-czna-users.error.passwordPolicy.".concat(rule);
    }
    if (info.message) {
        // Anchored on the backend's "User with <field> '...'" so a value that happens to
        // contain the other field's name cannot be misread (CZNA-1557).
        if (/^User with acronym '.*' already exists/i.test(info.message)) {
            return "ui-czna-users.error.duplicateAcronym";
        }
        if (/email.*already exists/i.test(info.message)) {
            return "ui-czna-users.error.duplicateEmail";
        }
        // CZNA-1604 — the organization (of the user or of an org-scoped role) was archived
        // between loading the form, which offers only active ones, and saving it.
        if (/^Organization '[^']*' is archived/i.test(info.message)) {
            return "ui-czna-users.error.organizationArchived";
        }
        if (/last system administrator/i.test(info.message)) {
            return "ui-czna-users.error.userLastAdmin";
        }
        // CZNA-1598 — the NK role rules the server enforces on a user or role write.
        if (/^Cannot move the user out of NK while they hold the NK supervisor role/i.test(info.message)) {
            return "ui-czna-users.error.nkSupervisorLeavesNationalLibrary";
        }
        if (/^The NK supervisor role \(national scope\) can be held only by a user of NK/i.test(info.message)) {
            return "ui-czna-users.validation.nkSupervisor.requiresNationalLibrary";
        }
        if (/^User already holds a role for authority type /i.test(info.message)) {
            return "ui-czna-users.error.authorityTypeRoleExists";
        }
    }
    // CZNA-1603: only the backend's code means "changed by another user" — any other 409
    // (an IllegalStateException) is a refusal that reloading would only repeat.
    if (isOptimisticLockConflict(info))
        return "ui-czna-users.error.conflict";
    if (info.status === 404)
        return "ui-czna-users.error.notFound";
    if (info.status === 502)
        return "ui-czna-users.error.externalService";
    // CZNA-1626: only now the status kind — a specific message above always outranks it.
    return (_a = getStatusMessageId(info)) !== null && _a !== void 0 ? _a : GENERIC;
};
export var getOrganizationErrorMessageId = function (info) {
    var _a;
    if (info.message) {
        if (/name.*already exists/i.test(info.message)) {
            return "ui-czna-users.error.duplicateOrganizationName";
        }
        if (/sigla.*already exists/i.test(info.message)) {
            return "ui-czna-users.error.duplicateOrganizationSigla";
        }
        if (/active user/i.test(info.message)) {
            return "ui-czna-users.error.organizationHasActiveUsers";
        }
        if (/last system administrator/i.test(info.message)) {
            return "ui-czna-users.error.organizationLastAdmin";
        }
    }
    // CZNA-1603: only the backend's code means "changed by another user" — a bare 409 is some
    // other refusal, and calling it a concurrent edit sends the admin to reload for nothing.
    if (isOptimisticLockConflict(info)) {
        return "ui-czna-users.error.optimisticLockConflict";
    }
    if (info.status === 404)
        return "ui-czna-users.error.notFound";
    // CZNA-1626: only now the status kind — a specific message above always outranks it.
    return (_a = getStatusMessageId(info)) !== null && _a !== void 0 ? _a : GENERIC;
};
